/**
 * Itinerary Builder V2 — stay-based trip planner
 *
 * Layout: two-panel (builder/timeline left, map right)
 * Modes:  "build" — edit trip skeleton   "view" — day-by-day timeline
 */

import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import {
  PlusIcon, TrashIcon, MapPinIcon,
  ChevronDownIcon, ChevronUpIcon, SparklesIcon,
  BookmarkIcon, FolderOpenIcon, GlobeAltIcon,
  ExclamationTriangleIcon, CheckCircleIcon, XMarkIcon, ArrowPathIcon,
  LinkIcon, ClockIcon, DocumentTextIcon,
} from '@heroicons/react/24/outline';
import {
  itineraryV2API,
  type ItineraryV2Data, type Stay, type TransitSegment,
  type ItinLocation, type ItinActivity, type LodgingRec, type DiningRec,
  type SideTrip, type SideTripSuggestion, type TravelRequirement,
  type ItinerarySummary, type RouteSight, type TransitMode,
} from '../services/api';
import { ItineraryMapV2 } from '../components/itinerary/ItineraryMapV2';
import {
  QuickPlanForm, StayExplorePanel, TransitExplorePanel, ExploreBottomStrip,
  type QuickPlanRequest,
} from '../components/itinerary/QuickPlan';
import { TripBriefing } from '../components/itinerary/TripBriefing';
import { useActivitiesStore } from '../store/activitiesStore';

// ─────────────────────────────────────────────────────────────────────────────
// Types & helpers
// ─────────────────────────────────────────────────────────────────────────────

type Segment = Stay | TransitSegment;
type PanelMode = 'plan' | 'explore' | 'build' | 'view';

const STAY_COLORS_HEX = [
  '#0E7490', '#7C3AED', '#B45309', '#065F46', '#9F1239',
  '#1D4ED8', '#92400E', '#155E75', '#4C1D95', '#064E3B',
];
const stayColorFor = (idx: number) => STAY_COLORS_HEX[idx % STAY_COLORS_HEX.length];

const TRAVEL_STYLES = ['relaxed', 'moderate', 'packed'] as const;
const BUDGET_LEVELS = ['budget', 'midrange', 'luxury'] as const;
const GROUP_TYPES = ['solo', 'couple', 'family', 'group'] as const;
const INTERESTS = ['history', 'food', 'nature', 'beaches', 'art', 'adventure', 'nightlife', 'shopping', 'architecture', 'wildlife'];
const TRANSIT_MODES: { value: TransitMode; label: string; icon: string }[] = [
  { value: 'drive', label: 'Drive', icon: '🚗' },
  { value: 'fly',   label: 'Fly',   icon: '✈️' },
  { value: 'train', label: 'Train', icon: '🚆' },
  { value: 'bus',   label: 'Bus',   icon: '🚌' },
  { value: 'ferry', label: 'Ferry', icon: '⛴️' },
];

function genId(): string {
  return Math.random().toString(36).slice(2, 10);
}

function emptyLocation(): ItinLocation {
  return { city: '', country: '' };
}

function emptyStay(id?: string): Stay {
  return {
    type: 'stay', id: id || genId(),
    location: emptyLocation(),
    check_in: '', check_out: '', nights: 2,
    lodging: { want_recommendations: true },
    activities: [], dining_recommendations: [],
    travel_tips: [], notes: '', enriched: false,
  };
}

function emptyTransit(id?: string): TransitSegment {
  return {
    type: 'transit', id: id || genId(),
    from_location: emptyLocation(), to_location: emptyLocation(),
    departure_time: '', arrival_time: '', mode: 'drive',
  };
}

function addMinutes(isoStr: string, minutes: number): string {
  if (!isoStr || !minutes) return '';
  try {
    const d = new Date(isoStr);
    d.setMinutes(d.getMinutes() + minutes);
    return d.toISOString().slice(0, 16);
  } catch { return ''; }
}

function computeCheckOut(checkIn: string, nights: number): string {
  if (!checkIn || nights <= 0) return '';
  try {
    const d = new Date(checkIn);
    d.setDate(d.getDate() + nights);
    d.setHours(10, 0, 0, 0);
    return d.toISOString().slice(0, 16);
  } catch { return ''; }
}

function formatDT(isoStr: string): string {
  if (!isoStr) return '—';
  try {
    return new Date(isoStr).toLocaleString('en-US', {
      month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true,
    });
  } catch { return isoStr; }
}

function formatDate(isoStr: string): string {
  if (!isoStr) return '—';
  try {
    const s = isoStr.length === 10 ? isoStr + 'T12:00' : isoStr;
    return new Date(s).toLocaleDateString('en-US', {
      weekday: 'short', month: 'short', day: 'numeric',
    });
  } catch { return isoStr; }
}

function formatDuration(min: number): string {
  const h = Math.floor(min / 60);
  const m = Math.round(min % 60);
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

/**
 * Haversine great-circle distance in km. Used to synthesize a transit duration
 * estimate locally when the server-side calculateTransit call fails — this keeps
 * the date cascade from breaking and leaving downstream stays un-enrichable.
 */
function haversineKm(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371;
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a = Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.asin(Math.sqrt(a));
}

function estimateTransitMinutes(
  fromLat: number, fromLon: number, toLat: number, toLon: number,
  mode: TransitMode,
): { duration: number; distance: number } {
  const straight = haversineKm(fromLat, fromLon, toLat, toLon);
  if (mode === 'fly') {
    return { duration: Math.max(60, Math.round((straight / 800) * 60) + 30), distance: Math.round(straight) };
  }
  if (mode === 'train') {
    const km = straight * 1.15;
    return { duration: Math.round((km / 110) * 60), distance: Math.round(km) };
  }
  if (mode === 'bus') {
    const km = straight * 1.4;
    return { duration: Math.round((km / 55) * 60), distance: Math.round(km) };
  }
  if (mode === 'ferry') {
    const km = straight * 1.1;
    return { duration: Math.round((km / 35) * 60) + 60, distance: Math.round(km) };
  }
  // drive (default) — 80 km/h on ~1.3x road factor
  const km = straight * 1.3;
  return { duration: Math.round((km / 80) * 60), distance: Math.round(km) };
}

/** Cascade dates forward through the segment array */
function cascadeDates(segments: Segment[]): Segment[] {
  return segments.map((seg, idx) => {
    if (idx === 0) return seg;
    const prev = segments[idx - 1];
    if (!prev) return seg;

    if (seg.type === 'transit') {
      const prevStay = prev.type === 'stay' ? (prev as Stay) : null;
      const t = seg as TransitSegment;
      // Use user-overridden departure if set; otherwise derive from previous stay's checkout
      const departure = t.departure_override ? t.departure_time : (prevStay?.check_out || t.departure_time);
      const arrival = t.duration_minutes && departure
        ? addMinutes(departure, t.duration_minutes)
        : t.arrival_time;
      return { ...t, departure_time: departure, arrival_time: arrival,
        from_location: prevStay?.location || t.from_location } as TransitSegment;
    }

    if (seg.type === 'stay') {
      const prevTransit = prev.type === 'transit' ? (prev as TransitSegment) : null;
      const checkIn = prevTransit?.arrival_time || seg.check_in;
      const checkOut = checkIn ? computeCheckOut(checkIn, (seg as Stay).nights) : seg.check_out;
      return { ...seg, check_in: checkIn, check_out: checkOut } as Stay;
    }

    return seg;
  });
}

function collectCountries(segments: Segment[]): string[] {
  const seen = new Set<string>();
  segments.forEach(s => {
    if (s.type === 'stay' && (s as Stay).location.country) seen.add((s as Stay).location.country);
  });
  return Array.from(seen);
}

// ─────────────────────────────────────────────────────────────────────────────
// Small sub-components
// ─────────────────────────────────────────────────────────────────────────────

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-1">{children}</p>
);

const Pill: React.FC<{ children: React.ReactNode; active?: boolean; onClick?: () => void }> = ({ children, active, onClick }) => (
  <button
    onClick={onClick}
    type="button"
    className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
      active
        ? 'bg-teal-600 text-white border-teal-600'
        : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:border-teal-400'
    }`}
  >
    {children}
  </button>
);

const Badge: React.FC<{ children: React.ReactNode; color?: string }> = ({ children, color = 'teal' }) => {
  const cls: Record<string, string> = {
    teal: 'bg-teal-50 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300',
    amber: 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
    purple: 'bg-purple-50 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
    gray: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
    green: 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300',
    red: 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300',
  };
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${cls[color] || cls.gray}`}>
      {children}
    </span>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// LocationSearch
// ─────────────────────────────────────────────────────────────────────────────

interface LocationSearchProps {
  value: ItinLocation;
  onChange: (loc: ItinLocation) => void;
  placeholder?: string;
  className?: string;
}

const LocationSearch: React.FC<LocationSearchProps> = ({
  value, onChange, placeholder = 'City, Country', className = '',
}) => {
  const [query, setQuery] = useState(value.city ? `${value.city}${value.country ? ', ' + value.country : ''}` : '');
  const [results, setResults] = useState<Array<{ city: string; country: string; lat?: number; lon?: number }>>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isGeocoding, setIsGeocoding] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Sync query when value changes externally
  useEffect(() => {
    if (value.city && !query) {
      setQuery(`${value.city}${value.country ? ', ' + value.country : ''}`);
    }
  }, [value.city]);

  const searchKnown = useCallback(async (q: string) => {
    if (q.length < 2) { setResults([]); return; }
    try {
      const resp = await fetch(`/api/destinations/search?q=${encodeURIComponent(q)}&limit=6`);
      if (resp.ok) {
        const data = await resp.json();
        setResults((data.destinations || []).map((d: any) => ({
          city: d.city, country: d.country, lat: d.lat, lon: d.lon,
        })));
      }
    } catch { setResults([]); }
  }, []);

  const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    setQuery(v);
    setShowDropdown(true);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => searchKnown(v), 300);
  };

  const handleSelect = (item: { city: string; country: string; lat?: number; lon?: number }) => {
    setQuery(`${item.city}, ${item.country}`);
    onChange({ city: item.city, country: item.country, lat: item.lat, lon: item.lon });
    setShowDropdown(false);
    setResults([]);
  };

  const geocodeQuery = useCallback(async (rawQuery: string) => {
    const parts = rawQuery.split(',').map(p => p.trim());
    if (parts.length < 2) return;
    const city = parts[0];
    const country = parts.slice(1).join(', ');
    setIsGeocoding(true);
    try {
      const r = await itineraryV2API.geocode(city, country);
      onChange({ city, country, lat: r.lat, lon: r.lon });
      setQuery(`${city}, ${country}`);
    } catch {
      onChange({ city, country });
    }
    setIsGeocoding(false);
    setShowDropdown(false);
  }, [onChange]);

  const handleBlur = () => {
    setTimeout(() => setShowDropdown(false), 200);
    if (!value.city && query.trim().includes(',')) {
      geocodeQuery(query.trim());
    }
  };

  return (
    <div className={`relative ${className}`}>
      <div className="relative">
        <MapPinIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
        <input
          type="text"
          value={query}
          onChange={handleInput}
          onBlur={handleBlur}
          onFocus={() => results.length > 0 && setShowDropdown(true)}
          placeholder={placeholder}
          className="w-full pl-8 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-teal-500"
        />
        {isGeocoding && (
          <ArrowPathIcon className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-teal-500 animate-spin pointer-events-none" />
        )}
      </div>
      {showDropdown && (results.length > 0 || query.includes(',')) && (
        <div className="absolute z-50 left-0 right-0 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl overflow-hidden">
          {results.map((r, i) => (
            <button
              key={i}
              type="button"
              onMouseDown={() => handleSelect(r)}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-700 text-left"
            >
              <MapPinIcon className="w-3.5 h-3.5 text-teal-500 flex-shrink-0" />
              <span>{r.city}, {r.country}</span>
            </button>
          ))}
          {query.includes(',') && (
            <button
              type="button"
              onMouseDown={() => geocodeQuery(query)}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-teal-600 dark:text-teal-400 hover:bg-teal-50 dark:hover:bg-teal-900/20 border-t border-gray-100 dark:border-gray-700"
            >
              <GlobeAltIcon className="w-3.5 h-3.5 flex-shrink-0" />
              <span>Use "{query}" (geocode)</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────────────────────

export const ItineraryBuilder: React.FC = () => {
  const [mode, setMode] = useState<PanelMode>('plan');
  const [itin, setItin] = useState<ItineraryV2Data>({
    title: '', segments: [emptyStay()], side_trips: [],
  });
  const [travelStyle, setTravelStyle] = useState('moderate');
  const [budgetLevel, setBudgetLevel] = useState('midrange');
  const [groupType, setGroupType] = useState('couple');
  const [interests, setInterests] = useState<string[]>([]);
  const [allowedModes, setAllowedModes] = useState<TransitMode[]>(['drive', 'ferry', 'train', 'fly']);
  const [defaultMode, setDefaultMode] = useState<TransitMode>('drive');
  const [startDate, setStartDate] = useState<string>('');

  const [savedList, setSavedList] = useState<ItinerarySummary[]>([]);
  const [loadedId, setLoadedId] = useState<string | null>(null);
  const [localActivityId, setLocalActivityId] = useState<string | null>(null);
  const location = useLocation();
  const { addActivity, updateActivity, getActivityById, setCurrentActivityId } = useActivitiesStore();

  // Ref mirror of `itin` so background tasks (Promise.all, etc.) can read the
  // latest streamed-in state without stale closures.
  const itinRef = useRef<ItineraryV2Data>(itin);
  useEffect(() => { itinRef.current = itin; }, [itin]);
  const [selectedStayId, setSelectedStayId] = useState<string | null>(null);
  const [selectedTransitId, setSelectedTransitId] = useState<string | null>(null);
  const [expandedStayId, setExpandedStayId] = useState<string | null>(null);
  const [expandedTransitId, setExpandedTransitId] = useState<string | null>(null);

  const [enrichingId, setEnrichingId] = useState<string | null>(null);
  const [calcTransitId, setCalcTransitId] = useState<string | null>(null);
  const [loadingRouteSightsId, setLoadingRouteSightsId] = useState<string | null>(null);
  const [loadingTravelReqs, setLoadingTravelReqs] = useState(false);
  const [loadingSideTrips, setLoadingSideTrips] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingList, setIsLoadingList] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatingMessage, setGeneratingMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [showSavedPanel, setShowSavedPanel] = useState(false);
  const [showBriefing, setShowBriefing] = useState(false);

  const stays = useMemo(
    () => itin.segments.filter((s): s is Stay => s.type === 'stay'),
    [itin.segments]
  );

  // ── Timeline days (for view mode) ─────────────────────────────────────────
  const timelineDays = useMemo(() => {
    const days: Array<{
      date: string; type: 'stay' | 'transit';
      stay?: Stay; transit?: TransitSegment;
      dayLabel: string; activities: ItinActivity[];
      isArrival: boolean; isDeparture: boolean;
    }> = [];

    itin.segments.forEach(seg => {
      if (seg.type === 'transit') {
        days.push({
          date: seg.departure_time?.slice(0, 10) || '',
          type: 'transit', transit: seg as TransitSegment,
          dayLabel: `${(seg as TransitSegment).from_location.city || '?'} → ${(seg as TransitSegment).to_location.city || '?'}`,
          activities: [], isArrival: false, isDeparture: false,
        });
        return;
      }
      const stay = seg as Stay;
      if (!stay.check_in) return;
      try {
        const ci = new Date(stay.check_in);
        const co = stay.check_out ? new Date(stay.check_out) : null;
        let cur = new Date(ci);
        let dayNum = 1;
        while (true) {
          const dateStr = cur.toISOString().slice(0, 10);
          const isArrival = dayNum === 1;
          const isDeparture = co ? cur.toDateString() === co.toDateString() : false;
          const dayActs = stay.activities.filter(a => a.date === dateStr);
          days.push({ date: dateStr, type: 'stay', stay, dayLabel: isArrival ? `Arrive ${stay.location.city}` : isDeparture ? `Depart ${stay.location.city}` : stay.location.city, activities: dayActs, isArrival, isDeparture });
          if (co && cur >= co) break;
          cur.setDate(cur.getDate() + 1);
          if (++dayNum > 35) break;
        }
      } catch { /* skip */ }
    });
    return days;
  }, [itin.segments]);

  // ── Segment mutation ───────────────────────────────────────────────────────

  const updateSegment = useCallback((id: string, patch: Partial<Stay> | Partial<TransitSegment>) => {
    setItin(prev => {
      const segs = prev.segments.map(s => s.id === id ? { ...s, ...(patch as any) } as Segment : s);
      return { ...prev, segments: cascadeDates(segs) };
    });
  }, []);

  const addStop = useCallback(() => {
    setItin(prev => {
      const lastStay = [...prev.segments].reverse().find((s): s is Stay => s.type === 'stay');
      const transit: TransitSegment = {
        ...emptyTransit(), from_location: lastStay?.location || emptyLocation(),
        departure_time: lastStay?.check_out || '',
      };
      const stay: Stay = { ...emptyStay() };
      return { ...prev, segments: cascadeDates([...prev.segments, transit, stay]) };
    });
  }, []);

  const removeStop = useCallback((stayId: string) => {
    setItin(prev => {
      const idx = prev.segments.findIndex(s => s.id === stayId);
      if (idx < 0) return prev;
      const newSegs = [...prev.segments];
      newSegs.splice(idx, 1);
      if (idx > 0 && newSegs[idx - 1]?.type === 'transit') newSegs.splice(idx - 1, 1);
      return { ...prev, segments: cascadeDates(newSegs) };
    });
  }, []);

  const updateStayNights = useCallback((stayId: string, nights: number) => {
    setItin(prev => {
      const segs = prev.segments.map(s => {
        if (s.id !== stayId || s.type !== 'stay') return s;
        return { ...s, nights, check_out: computeCheckOut((s as Stay).check_in, nights) } as Stay;
      });
      return { ...prev, segments: cascadeDates(segs) };
    });
  }, []);

  const updateStayCheckIn = useCallback((stayId: string, checkIn: string) => {
    setItin(prev => {
      const segs = prev.segments.map(s => {
        if (s.id !== stayId || s.type !== 'stay') return s;
        return { ...s, check_in: checkIn, check_out: computeCheckOut(checkIn, (s as Stay).nights) } as Stay;
      });
      return { ...prev, segments: cascadeDates(segs) };
    });
  }, []);

  const updateTransitTo = useCallback((transitId: string, loc: ItinLocation) => {
    setItin(prev => {
      const idx = prev.segments.findIndex(s => s.id === transitId);
      const segs = prev.segments.map((s, i) => {
        if (s.id === transitId) return { ...s, to_location: loc } as TransitSegment;
        if (i === idx + 1 && s.type === 'stay') return { ...s, location: loc } as Stay;
        return s;
      });
      return { ...prev, segments: segs };
    });
  }, []);

  const updateTransitDeparture = useCallback((transitId: string, departure: string) => {
    setItin(prev => {
      const segs = prev.segments.map(s => {
        if (s.id !== transitId || s.type !== 'transit') return s;
        const t = s as TransitSegment;
        const arrival = t.duration_minutes && departure ? addMinutes(departure, t.duration_minutes) : t.arrival_time;
        return { ...t, departure_time: departure, arrival_time: arrival, departure_override: true } as TransitSegment;
      });
      return { ...prev, segments: cascadeDates(segs) };
    });
  }, []);

  const clearTransitDeparture = useCallback((transitId: string) => {
    setItin(prev => {
      const segs = prev.segments.map(s => {
        if (s.id !== transitId || s.type !== 'transit') return s;
        return { ...(s as TransitSegment), departure_override: false } as TransitSegment;
      });
      return { ...prev, segments: cascadeDates(segs) };
    });
  }, []);

  // ── Transit calculation ───────────────────────────────────────────────────

  const calculateTransit = useCallback(async (transitId: string) => {
    const transit = itin.segments.find(s => s.id === transitId) as TransitSegment | undefined;
    if (!transit) return;
    const { from_location: from, to_location: to, mode: tMode } = transit;
    if (!from.lat || !to.lat) {
      setError('Both locations need coordinates. Select from the dropdown or type "City, Country" and press Tab.');
      return;
    }
    setCalcTransitId(transitId);
    setError(null);
    try {
      const result = await itineraryV2API.calculateTransit({
        from_lat: from.lat, from_lon: from.lon!,
        to_lat: to.lat, to_lon: to.lon!, mode: tMode,
      });
      setItin(prev => {
        const segs = prev.segments.map(s => {
          if (s.id !== transitId) return s;
          const t = s as TransitSegment;
          const arrival = addMinutes(t.departure_time || '', result.duration_minutes);
          return { ...t, duration_minutes: result.duration_minutes, distance_km: result.distance_km, geometry: result.geometry || undefined, arrival_time: arrival } as TransitSegment;
        });
        return { ...prev, segments: cascadeDates(segs) };
      });
      setSuccessMsg(`Transit calculated: ${formatDuration(result.duration_minutes)}`);
      setTimeout(() => setSuccessMsg(null), 3000);
    } catch (e: any) {
      setError(`Transit calculation failed: ${e?.response?.data?.detail || e?.message || 'Unknown error'}`);
    } finally {
      setCalcTransitId(null);
    }
  }, [itin.segments]);

  // ── Enrich Stay ───────────────────────────────────────────────────────────
  //
  // Self-heals common breakages before calling the enrichment API:
  //   1. Missing coords → geocodes the city/country
  //   2. Missing dates  → re-cascades the whole itinerary using the start_date
  //      anchor + available transit durations (synthesizing estimates where
  //      needed). Covers the case where an earlier leg's transit failed and
  //      left downstream stays without valid check-in/out.

  const enrichStay = useCallback(async (stayId: string) => {
    let stay = itin.segments.find(s => s.id === stayId) as Stay | undefined;
    if (!stay || !stay.location.city) {
      setError('This stop has no location — edit it in Plan or Advanced mode first.');
      return;
    }

    setEnrichingId(stayId);
    setError(null);

    // --- Self-heal coords --------------------------------------------------
    let repairedItin: ItineraryV2Data | null = null;
    if (!stay.location.lat || !stay.location.lon) {
      try {
        const g = await itineraryV2API.geocode(stay.location.city, stay.location.country || '');
        const newLoc: ItinLocation = { ...stay.location, lat: g.lat, lon: g.lon };
        setItin(prev => {
          const segs = prev.segments.map(s =>
            s.id === stayId && s.type === 'stay' ? ({ ...(s as Stay), location: newLoc } as Stay) : s
          );
          return { ...prev, segments: segs };
        });
        stay = { ...stay, location: newLoc };
      } catch {
        // keep going — enrichment can still succeed by city name alone
      }
    }

    // --- Self-heal dates ---------------------------------------------------
    if (!stay.check_in || !stay.check_out) {
      const startAnchor = itin.plan_preferences?.start_date || startDate;
      if (!startAnchor) {
        setEnrichingId(null);
        setError('Start date missing — open Plan mode and set a start date, then try again.');
        return;
      }

      // Walk segments in order, synthesizing missing durations so every stay
      // ends up with a check_in/check_out.
      const firstCheckIn = `${startAnchor}T15:00`;
      const repaired: Segment[] = itin.segments.map(s => ({ ...(s as any) }));
      let prevStay: Stay | null = null;

      for (let i = 0; i < repaired.length; i++) {
        const seg = repaired[i];
        if (seg.type === 'stay') {
          const st = seg as Stay;
          if (!prevStay) {
            // First stay anchors on the trip start date
            if (!st.check_in) st.check_in = firstCheckIn;
            if (!st.check_out) st.check_out = computeCheckOut(st.check_in, st.nights) || st.check_out;
          } else {
            const prevT = repaired[i - 1] as TransitSegment | undefined;
            if (prevT?.type === 'transit') {
              // Ensure the preceding transit has a duration
              if (!prevT.duration_minutes) {
                if (prevStay.location.lat && st.location.lat) {
                  const est = estimateTransitMinutes(
                    prevStay.location.lat, prevStay.location.lon!,
                    st.location.lat!, st.location.lon!, prevT.mode,
                  );
                  prevT.duration_minutes = est.duration;
                  prevT.distance_km = est.distance;
                } else {
                  prevT.duration_minutes = 180; // 3h default if we can't estimate
                }
              }
              if (!prevT.departure_time) prevT.departure_time = prevStay.check_out || '';
              if (!prevT.arrival_time && prevT.departure_time) {
                prevT.arrival_time = addMinutes(prevT.departure_time, prevT.duration_minutes!);
              }
              if (!st.check_in) st.check_in = prevT.arrival_time || firstCheckIn;
              if (!st.check_out) st.check_out = computeCheckOut(st.check_in, st.nights) || '';
            }
          }
          prevStay = st;
        }
      }

      const cascaded = cascadeDates(repaired);
      const freshStay = cascaded.find(s => s.id === stayId) as Stay | undefined;
      if (!freshStay || !freshStay.check_in || !freshStay.check_out) {
        setEnrichingId(null);
        setError('Could not determine dates for this stop. Edit it in Advanced mode.');
        return;
      }
      repairedItin = { ...itin, segments: cascaded };
      setItin(repairedItin);
      stay = freshStay;
    }

    // --- Enrich -----------------------------------------------------------
    try {
      const result = await itineraryV2API.enrichStay({
        city: stay.location.city, country: stay.location.country,
        lat: stay.location.lat, lon: stay.location.lon,
        check_in: stay.check_in, check_out: stay.check_out,
        travel_style: travelStyle, budget_level: budgetLevel,
        interests, group_type: groupType, notes: stay.notes,
        want_lodging: stay.lodging.want_recommendations, want_dining: true,
      });
      const firstAct = result.activities[0];
      const locPatch = (!stay.location.lat && firstAct?.lat)
        ? { location: { ...stay.location, lat: firstAct.lat, lon: firstAct.lon } }
        : {};
      updateSegment(stayId, {
        activities: result.activities,
        dining_recommendations: result.dining_recommendations,
        travel_tips: result.travel_tips,
        lodging: { ...stay.lodging, recommendations: stay.lodging.want_recommendations ? result.lodging_recommendations : undefined },
        enriched: true,
        ...locPatch,
      });
      setSuccessMsg(`${stay.location.city} enriched — ${result.activities.length} activities`);
      setTimeout(() => setSuccessMsg(null), 3000);
    } catch (e: any) {
      setError(`Enrichment failed: ${e?.response?.data?.detail || e?.message || 'Unknown error'}`);
    } finally {
      setEnrichingId(null);
    }
  }, [itin, startDate, travelStyle, budgetLevel, interests, groupType, updateSegment]);

  // ── Side trips ────────────────────────────────────────────────────────────

  const fetchSideTrips = useCallback(async (stayId: string) => {
    const stay = itin.segments.find(s => s.id === stayId) as Stay | undefined;
    if (!stay?.location.city) return;
    setLoadingSideTrips(stayId);
    try {
      const result = await itineraryV2API.sideTripSuggestions({
        from_city: stay.location.city, from_country: stay.location.country,
        from_lat: stay.location.lat, from_lon: stay.location.lon,
        duration_preference: 'both',
      });
      updateSegment(stayId, { _sideTripSuggestions: result.suggestions } as any);
    } catch (e: any) {
      setError(`Side trip suggestions failed: ${e?.message || 'Unknown error'}`);
    } finally {
      setLoadingSideTrips(null);
    }
  }, [itin.segments, updateSegment]);

  const addSideTrip = useCallback((stayId: string, suggestion: SideTripSuggestion) => {
    const stay = itin.segments.find(s => s.id === stayId) as Stay | undefined;
    const sideTrip: SideTrip = {
      id: genId(), from_stay_id: stayId, suggestion,
      date: stay?.check_in?.slice(0, 10) || '', confirmed: true,
    };
    setItin(prev => ({ ...prev, side_trips: [...prev.side_trips, sideTrip] }));
  }, [itin.segments]);

  const removeSideTrip = useCallback((id: string) => {
    setItin(prev => ({ ...prev, side_trips: prev.side_trips.filter(s => s.id !== id) }));
  }, []);

  // ── Travel requirements ───────────────────────────────────────────────────

  const loadTravelRequirements = useCallback(async () => {
    const countries = collectCountries(itin.segments);
    if (!countries.length) { setError('Add at least one stay with a country first.'); return; }
    setLoadingTravelReqs(true);
    setError(null);
    try {
      const result = await itineraryV2API.travelRequirements(countries);
      setItin(prev => ({ ...prev, travel_requirements: result.requirements }));
    } catch (e: any) {
      setError(`Travel requirements failed: ${e?.message || 'Unknown error'}`);
    } finally {
      setLoadingTravelReqs(false);
    }
  }, [itin.segments]);

  // ── Save / Load ───────────────────────────────────────────────────────────

  const loadSavedList = useCallback(async () => {
    setIsLoadingList(true);
    try { setSavedList(await itineraryV2API.list()); } catch { }
    finally { setIsLoadingList(false); }
  }, []);

  useEffect(() => { loadSavedList(); }, []);

  const saveItinerary = useCallback(async () => {
    if (!itin.title.trim()) { setError('Please enter a trip title.'); return; }
    setIsSaving(true); setError(null);
    try {
      // Persist current preferences alongside itinerary data
      const dataWithPrefs: ItineraryV2Data = {
        ...itin,
        plan_preferences: {
          travel_style: travelStyle,
          budget_level: budgetLevel,
          group_type: groupType,
          interests,
          allowed_modes: allowedModes,
          default_mode: defaultMode,
          start_date: startDate || itin.plan_preferences?.start_date || '',
        },
      };
      const payload = { title: itin.title, data: dataWithPrefs };
      let currentId = loadedId;
      if (loadedId) {
        await itineraryV2API.update(loadedId, payload);
      } else {
        const r = await itineraryV2API.create(payload);
        currentId = r.id;
        setLoadedId(r.id);
      }

      // Keep the sidebar ACTIVITIES entry in sync (rename, bump savedAt, or
      // create one if the user is saving a freshly-built trip for the first
      // time).
      if (localActivityId) {
        updateActivity(localActivityId, {
          name: itin.title,
          savedAt: new Date(),
          data: currentId ? { itineraryId: currentId } : { itineraryData: dataWithPrefs },
        });
      } else {
        const actId = `itin-${Date.now()}`;
        addActivity({
          id: actId,
          name: itin.title,
          activityType: 'itinerary_builder',
          savedAt: new Date(),
          data: currentId ? { itineraryId: currentId } : { itineraryData: dataWithPrefs },
        });
        setLocalActivityId(actId);
        setCurrentActivityId(actId);
      }

      setSuccessMsg('Saved!');
      setTimeout(() => setSuccessMsg(null), 2000);
      loadSavedList();
    } catch (e: any) {
      setError(`Save failed: ${e?.response?.data?.detail || e?.message || 'Unknown error'}`);
    } finally {
      setIsSaving(false);
    }
  }, [itin, loadedId, loadSavedList, travelStyle, budgetLevel, groupType, interests, allowedModes, defaultMode, startDate, localActivityId, addActivity, updateActivity, setCurrentActivityId]);

  // Apply loaded itinerary data + its plan_preferences to local state.
  const applyLoadedItin = useCallback((data: ItineraryV2Data) => {
    setItin(data);
    if (data.plan_preferences) {
      setTravelStyle(data.plan_preferences.travel_style);
      setBudgetLevel(data.plan_preferences.budget_level);
      setGroupType(data.plan_preferences.group_type);
      setInterests(data.plan_preferences.interests);
      setAllowedModes(data.plan_preferences.allowed_modes);
      setDefaultMode(data.plan_preferences.default_mode);
      setStartDate(data.plan_preferences.start_date);
    }
    setSelectedStayId(null);
    setSelectedTransitId(null);
  }, []);

  const loadItinerary = useCallback(async (id: string) => {
    try {
      const row = await itineraryV2API.get(id);
      applyLoadedItin(row.data as ItineraryV2Data);
      setLoadedId(id);
      setShowSavedPanel(false);
      setMode('explore');

      // Re-link to the matching sidebar activity if one exists, otherwise
      // create one so this trip also appears in the ACTIVITIES list.
      const existing = useActivitiesStore.getState().activities.find(
        a => a.activityType === 'itinerary_builder' && (a.data as any)?.itineraryId === id
      );
      if (existing) {
        setLocalActivityId(existing.id);
        setCurrentActivityId(existing.id);
        updateActivity(existing.id, { name: row.title, savedAt: new Date() });
      } else {
        const actId = `itin-${Date.now()}`;
        addActivity({
          id: actId,
          name: row.title,
          activityType: 'itinerary_builder',
          savedAt: new Date(),
          data: { itineraryId: id },
        });
        setLocalActivityId(actId);
        setCurrentActivityId(actId);
      }
    } catch (e: any) {
      setError(`Load failed: ${e?.message || 'Unknown error'}`);
    }
  }, [applyLoadedItin, addActivity, updateActivity, setCurrentActivityId]);

  // ── Load from sidebar ACTIVITIES click ────────────────────────────────────
  //
  // The sidebar passes `loadActivityId` via react-router location state. We
  // look it up in the activities store, then either fetch the backend
  // itinerary (preferred) or fall back to the locally-cached data blob.

  useEffect(() => {
    const navState = location.state as { loadActivityId?: string } | null;
    if (!navState?.loadActivityId) return;
    const activity = getActivityById(navState.loadActivityId);
    if (!activity || activity.activityType !== 'itinerary_builder') return;

    const itinId = (activity.data as any)?.itineraryId;
    const cached = (activity.data as any)?.itineraryData as ItineraryV2Data | undefined;

    (async () => {
      if (itinId) {
        try {
          const row = await itineraryV2API.get(itinId);
          applyLoadedItin(row.data as ItineraryV2Data);
          setLoadedId(itinId);
        } catch {
          // Row deleted server-side — fall back to cached data if any
          if (cached) applyLoadedItin(cached);
        }
      } else if (cached) {
        applyLoadedItin(cached);
      }
      setLocalActivityId(activity.id);
      setCurrentActivityId(activity.id);
      setMode('explore');
    })();

    // Clear location state so re-renders don't re-load
    window.history.replaceState({}, document.title);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const deleteItinerary = useCallback(async (id: string) => {
    try {
      await itineraryV2API.delete(id);
      if (loadedId === id) {
        setItin({ title: '', segments: [emptyStay()], side_trips: [] });
        setLoadedId(null);
        setLocalActivityId(null);
        setCurrentActivityId(null);
      }
      // Remove the matching sidebar ACTIVITIES entry so it doesn't linger as a
      // dead link pointing at a deleted server-side row.
      const store = useActivitiesStore.getState();
      const linked = store.activities.find(
        a => a.activityType === 'itinerary_builder' && (a.data as any)?.itineraryId === id
      );
      if (linked) store.removeActivity(linked.id);
      loadSavedList();
    } catch { }
  }, [loadedId, loadSavedList, setCurrentActivityId]);

  const newItinerary = useCallback(() => {
    setItin({ title: '', segments: [emptyStay()], side_trips: [] });
    setLoadedId(null);
    setLocalActivityId(null);
    setCurrentActivityId(null);
    setMode('plan');
    setExpandedStayId(null); setSelectedStayId(null); setSelectedTransitId(null);
  }, [setCurrentActivityId]);

  // ── Plan → Generate orchestrator ───────────────────────────────────────────
  //
  // Takes destinations + nights + preferences from QuickPlanForm and builds the
  // full itinerary in parallel: geocode → transit calc → enrich stays → route sights.
  // Switches UI to 'explore' mode as soon as the skeleton is ready; enrichment
  // streams in per-stop.

  const generateFromPlan = useCallback(async (req: QuickPlanRequest) => {
    setIsGenerating(true);
    setError(null);
    setGeneratingMessage('Looking up destinations…');

    // Persist preferences
    setTravelStyle(req.travel_style);
    setBudgetLevel(req.budget_level);
    setGroupType(req.group_type);
    setInterests(req.interests);
    setAllowedModes(req.allowed_modes);
    setDefaultMode(req.default_mode);
    setStartDate(req.start_date);

    try {
      // 1. Geocode every destination from its city/country string.
      //    We always re-geocode (rather than trusting cached coords from the
      //    form) so spelling fixes propagate, but we do it SEQUENTIALLY with a
      //    small stagger — Nominatim's free tier rate-limits ~1 req/sec, and
      //    parallel requests for multiple custom destinations end up silently
      //    dropped. Sequential adds ~1.5s for a 6-stop trip and is 100% reliable.
      const geocoded: QuickPlanRequest['destinations'] = [];
      for (const d of req.destinations) {
        try {
          const r = await itineraryV2API.geocode(d.city, d.country);
          geocoded.push({ ...d, lat: r.lat, lon: r.lon });
        } catch {
          // Geocode failed — fall back to whatever coords the form had.
          geocoded.push(d);
        }
        // Gentle stagger to respect Nominatim's rate limit
        await new Promise(r => setTimeout(r, 250));
      }

      // 2. Build skeleton segments
      const firstCheckIn = `${req.start_date}T15:00`;
      const segs: Segment[] = [];
      geocoded.forEach((d, i) => {
        if (i > 0) {
          const prev = segs[segs.length - 1] as Stay;
          segs.push({
            type: 'transit', id: genId(),
            from_location: prev.location,
            to_location: { city: d.city, country: d.country, lat: d.lat, lon: d.lon },
            departure_time: prev.check_out || '',
            arrival_time: '',
            mode: req.default_mode,
          });
        }
        const ci = i === 0 ? firstCheckIn : '';
        const co = i === 0 ? computeCheckOut(ci, d.nights) : '';
        segs.push({
          type: 'stay', id: genId(),
          location: { city: d.city, country: d.country, lat: d.lat, lon: d.lon },
          check_in: ci, check_out: co, nights: d.nights,
          lodging: { want_recommendations: true },
          activities: [], dining_recommendations: [],
          travel_tips: [], notes: '', enriched: false,
        });
      });

      // 3. Calculate transit for each leg (parallel). If the server call
      //    fails OR coords are missing, fall back to a local haversine
      //    estimate so the date chain never breaks — downstream stays still
      //    get valid check_in/check_out and can be enriched.
      setGeneratingMessage('Calculating travel times…');
      const transits = segs.filter((s): s is TransitSegment => s.type === 'transit');
      await Promise.all(transits.map(async t => {
        const fLat = t.from_location.lat, fLon = t.from_location.lon;
        const tLat = t.to_location.lat,   tLon = t.to_location.lon;
        const haveCoords = fLat != null && fLon != null && tLat != null && tLon != null;

        if (haveCoords) {
          try {
            const r = await itineraryV2API.calculateTransit({
              from_lat: fLat!, from_lon: fLon!,
              to_lat: tLat!,   to_lon: tLon!,
              mode: t.mode,
            });
            t.duration_minutes = r.duration_minutes;
            t.distance_km = r.distance_km;
            t.geometry = r.geometry || undefined;
            return;
          } catch {
            // Fall through to local estimate
          }
        }

        if (haveCoords) {
          const est = estimateTransitMinutes(fLat!, fLon!, tLat!, tLon!, t.mode);
          t.duration_minutes = est.duration;
          t.distance_km = est.distance;
          t.notes = t.notes || 'Estimate — server calculation failed. Recalculate from the leg detail panel.';
        } else {
          // No coords — assume a safe 3h default so the cascade still works
          t.duration_minutes = 180;
          t.notes = 'Estimate only — destination could not be located. Edit in Advanced mode if needed.';
        }
      }));

      const cascaded = cascadeDates(segs);

      const draftItin: ItineraryV2Data = {
        title: req.title,
        segments: cascaded,
        side_trips: [],
        plan_preferences: {
          travel_style: req.travel_style,
          budget_level: req.budget_level,
          group_type: req.group_type,
          interests: req.interests,
          allowed_modes: req.allowed_modes,
          default_mode: req.default_mode,
          start_date: req.start_date,
        },
      };

      setItin(draftItin);
      setMode('explore');

      // 3b. Auto-persist the skeleton so the trip shows up in the sidebar
      //     ACTIVITIES list immediately. Enrichment will update this row as
      //     data streams in.
      //
      //     If the user is REGENERATING an itinerary they already have open
      //     (localActivityId / loadedId already set), overwrite the existing
      //     backend row + activity entry in place — users iteratively refine
      //     their trips and don't want a new duplicate entry each time.
      const isRegen = !!loadedId || !!localActivityId;

      let persistedId: string | null = loadedId;
      try {
        if (loadedId) {
          await itineraryV2API.update(loadedId, { title: draftItin.title, data: draftItin });
        } else {
          const res = await itineraryV2API.create({ title: draftItin.title, data: draftItin });
          persistedId = res.id;
          setLoadedId(persistedId);
        }
        loadSavedList();
      } catch {
        // Not authed / offline — activity falls back to local data
        persistedId = loadedId; // preserve whatever we had
      }

      const dataField = persistedId
        ? { itineraryId: persistedId }
        : { itineraryData: draftItin };

      if (isRegen && localActivityId) {
        updateActivity(localActivityId, {
          name: draftItin.title,
          savedAt: new Date(),
          data: dataField,
        });
        setCurrentActivityId(localActivityId);
      } else {
        const actId = `itin-${Date.now()}`;
        addActivity({
          id: actId,
          name: draftItin.title,
          activityType: 'itinerary_builder',
          savedAt: new Date(),
          data: dataField,
        });
        setLocalActivityId(actId);
        setCurrentActivityId(actId);
      }

      // 4. Enrich stays (parallel, stream updates)
      setGeneratingMessage('Picking sights, lodging, and restaurants for each stop…');
      await Promise.all(
        cascaded.filter((s): s is Stay => s.type === 'stay').map(async stay => {
          if (!stay.location.city || !stay.check_in || !stay.check_out) return;
          try {
            const r = await itineraryV2API.enrichStay({
              city: stay.location.city, country: stay.location.country,
              lat: stay.location.lat, lon: stay.location.lon,
              check_in: stay.check_in, check_out: stay.check_out,
              travel_style: req.travel_style, budget_level: req.budget_level,
              interests: req.interests, group_type: req.group_type,
              want_lodging: true, want_dining: true,
            });
            setItin(prev => ({
              ...prev,
              segments: prev.segments.map(s => {
                if (s.id !== stay.id || s.type !== 'stay') return s;
                const cur = s as Stay;
                const firstAct = r.activities[0];
                const needsLocFix = !cur.location.lat && firstAct?.lat;
                return ({
                  ...cur,
                  activities: r.activities,
                  lodging: { ...cur.lodging, recommendations: r.lodging_recommendations },
                  dining_recommendations: r.dining_recommendations,
                  travel_tips: r.travel_tips,
                  enriched: true,
                  location: needsLocFix
                    ? { ...cur.location, lat: firstAct.lat, lon: firstAct.lon }
                    : cur.location,
                } as Stay);
              }),
            }));
          } catch {
            // continue; user can retry individual stops
          }
        })
      );

      // 5. Route sights for each ground/sea leg (parallel)
      setGeneratingMessage('Finding sights along the way…');
      await Promise.all(
        cascaded
          .filter((s): s is TransitSegment => s.type === 'transit' && s.mode !== 'fly')
          .map(async t => {
            try {
              const r = await itineraryV2API.routeSights({
                from_city: t.from_location.city, from_country: t.from_location.country,
                to_city: t.to_location.city, to_country: t.to_location.country,
                from_lat: t.from_location.lat, from_lon: t.from_location.lon,
                to_lat: t.to_location.lat, to_lon: t.to_location.lon,
                mode: t.mode, interests: req.interests,
              });
              setItin(prev => ({
                ...prev,
                segments: prev.segments.map(s =>
                  s.id === t.id
                    ? ({
                        ...(s as TransitSegment),
                        route_sights: r.sights,
                        route_sights_loaded: true,
                      } as TransitSegment)
                    : s
                ),
              }));
            } catch {
              // non-fatal
            }
          })
      );

      // 6. Final sync — persist the fully-enriched itinerary so the sidebar
      //    entry reflects the real content on reload. Use whichever activity
      //    we just created or reused in step 3b.
      const finalItin = itinRef.current;
      const targetActId = useActivitiesStore.getState().currentActivityId;
      if (persistedId) {
        try {
          await itineraryV2API.update(persistedId, { title: finalItin.title, data: finalItin });
          if (targetActId) {
            updateActivity(targetActId, { name: finalItin.title, savedAt: new Date() });
          }
          loadSavedList();
        } catch {
          // ignore — skeleton is already saved
        }
      } else if (targetActId) {
        updateActivity(targetActId, {
          name: finalItin.title,
          savedAt: new Date(),
          data: { itineraryData: finalItin },
        });
      }
    } catch (e: any) {
      setError(`Generation failed: ${e?.response?.data?.detail || e?.message || 'Unknown error'}`);
    } finally {
      setIsGenerating(false);
      setGeneratingMessage(null);
    }
  }, [addActivity, updateActivity, setCurrentActivityId, loadSavedList, loadedId, localActivityId]);

  // ── Explore-panel actions ──────────────────────────────────────────────────

  const changeTransitMode = useCallback(async (transitId: string, newMode: TransitMode) => {
    const t = itin.segments.find(s => s.id === transitId) as TransitSegment | undefined;
    if (!t || !t.from_location.lat || !t.to_location.lat) return;
    setCalcTransitId(transitId);
    try {
      const r = await itineraryV2API.calculateTransit({
        from_lat: t.from_location.lat!, from_lon: t.from_location.lon!,
        to_lat: t.to_location.lat!, to_lon: t.to_location.lon!, mode: newMode,
      });
      setItin(prev => {
        const segs = prev.segments.map(s => {
          if (s.id !== transitId) return s;
          const tt = s as TransitSegment;
          const dep = tt.departure_time || '';
          const arr = r.duration_minutes && dep ? addMinutes(dep, r.duration_minutes) : '';
          return {
            ...tt, mode: newMode,
            duration_minutes: r.duration_minutes,
            distance_km: r.distance_km,
            geometry: r.geometry || undefined,
            arrival_time: arr,
            // Reset route-sights: they were for a different mode
            route_sights: undefined,
            route_sights_loaded: false,
          } as TransitSegment;
        });
        return { ...prev, segments: cascadeDates(segs) };
      });
    } catch (e: any) {
      setError(`Mode change failed: ${e?.message || 'Unknown error'}`);
    } finally {
      setCalcTransitId(null);
    }
  }, [itin.segments]);

  const loadRouteSightsFor = useCallback(async (transitId: string) => {
    const t = itin.segments.find(s => s.id === transitId) as TransitSegment | undefined;
    if (!t) return;
    if (t.mode === 'fly') return;
    setLoadingRouteSightsId(transitId);
    try {
      const r = await itineraryV2API.routeSights({
        from_city: t.from_location.city, from_country: t.from_location.country,
        to_city: t.to_location.city, to_country: t.to_location.country,
        from_lat: t.from_location.lat, from_lon: t.from_location.lon,
        to_lat: t.to_location.lat, to_lon: t.to_location.lon,
        mode: t.mode, interests,
      });
      setItin(prev => ({
        ...prev,
        segments: prev.segments.map(s => s.id === transitId
          ? ({ ...(s as TransitSegment), route_sights: r.sights, route_sights_loaded: true } as TransitSegment)
          : s),
      }));
    } catch (e: any) {
      setError(`Route sights failed: ${e?.message || 'Unknown error'}`);
    } finally {
      setLoadingRouteSightsId(null);
    }
  }, [itin.segments, interests]);

  // Derived plan form initial values (only when the user actually has a populated itin)
  const planFormInitial = useMemo<Partial<QuickPlanRequest> | undefined>(() => {
    const realStays = stays.filter(s => s.location.city.trim().length > 0);
    if (realStays.length === 0) {
      // Still return prefs so re-planning preserves style/modes even with no destinations yet
      return {
        title: itin.title,
        start_date: startDate || itin.plan_preferences?.start_date || '',
        travel_style: travelStyle,
        budget_level: budgetLevel,
        group_type: groupType,
        interests,
        allowed_modes: allowedModes,
        default_mode: defaultMode,
      };
    }
    const dests = realStays.map(s => ({
      tempId: s.id,
      city: s.location.city,
      country: s.location.country,
      lat: s.location.lat, lon: s.location.lon,
      nights: s.nights,
    }));
    return {
      title: itin.title,
      start_date: startDate || itin.plan_preferences?.start_date || '',
      travel_style: travelStyle,
      budget_level: budgetLevel,
      group_type: groupType,
      interests,
      destinations: dests,
      allowed_modes: allowedModes,
      default_mode: defaultMode,
    };
  }, [itin.title, itin.plan_preferences, stays, startDate, travelStyle, budgetLevel, groupType, interests, allowedModes, defaultMode]);

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col h-full bg-gray-50 dark:bg-gray-900 overflow-hidden">

      {/* Top bar */}
      <div className="flex items-center gap-3 px-4 py-2.5 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <GlobeAltIcon className="w-5 h-5 text-teal-600 flex-shrink-0" />
          <input
            type="text"
            value={itin.title}
            onChange={e => setItin(prev => ({ ...prev, title: e.target.value }))}
            placeholder="Trip title (e.g. Adriatic Coast 2026)"
            className="flex-1 min-w-0 text-sm font-medium bg-transparent border-none outline-none text-gray-900 dark:text-white placeholder-gray-400"
          />
        </div>

        <div className="flex bg-gray-100 dark:bg-gray-700 rounded-lg p-0.5 flex-shrink-0">
          {([
            ['plan',    '📝 Plan'],
            ['explore', '🗺 Explore'],
            ['view',    '📋 Timeline'],
            ['build',   '✏️ Advanced'],
          ] as const).map(([m, lbl]) => (
            <button key={m} onClick={() => setMode(m as PanelMode)}
              className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${
                mode === m ? 'bg-white dark:bg-gray-600 text-teal-700 dark:text-teal-300 shadow-sm' : 'text-gray-500 dark:text-gray-400'
              }`}>
              {lbl}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-1.5 flex-shrink-0">
          {stays.some(s => s.location.city) && (
            <button onClick={() => setShowBriefing(true)}
              title="Open printable trip briefing"
              className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
              <DocumentTextIcon className="w-4 h-4" /><span className="hidden sm:inline">Briefing</span>
            </button>
          )}
          <button onClick={() => { setShowSavedPanel(v => !v); if (!showSavedPanel) loadSavedList(); }}
            className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
            <FolderOpenIcon className="w-4 h-4" /><span className="hidden sm:inline">Load</span>
          </button>
          <button onClick={saveItinerary} disabled={isSaving}
            className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-white bg-teal-600 hover:bg-teal-700 rounded-lg disabled:opacity-60">
            {isSaving ? <ArrowPathIcon className="w-4 h-4 animate-spin" /> : <BookmarkIcon className="w-4 h-4" />}
            <span className="hidden sm:inline">Save</span>
          </button>
          <button onClick={newItinerary}
            className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
            <PlusIcon className="w-4 h-4" /><span className="hidden sm:inline">New</span>
          </button>
        </div>
      </div>

      {/* Notifications */}
      {(error || successMsg) && (
        <div className={`flex items-center gap-2 px-4 py-2 text-sm flex-shrink-0 ${
          error ? 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300' : 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300'
        }`}>
          {error ? <ExclamationTriangleIcon className="w-4 h-4 flex-shrink-0" /> : <CheckCircleIcon className="w-4 h-4 flex-shrink-0" />}
          <span className="flex-1">{error || successMsg}</span>
          <button onClick={() => { setError(null); setSuccessMsg(null); }}><XMarkIcon className="w-4 h-4" /></button>
        </div>
      )}

      {/* Trip Briefing overlay (global, works in any mode) */}
      {showBriefing && (
        <TripBriefing
          itineraryId={loadedId || undefined}
          itineraryData={!loadedId ? itin : undefined}
          tripTitle={itin.title || 'Trip'}
          onClose={() => setShowBriefing(false)}
        />
      )}

      {/* Saved-itineraries modal (global, works in any mode) */}
      {showSavedPanel && mode !== 'build' && mode !== 'view' && (
        <div className="fixed inset-0 z-[1100] bg-black/40 flex items-center justify-center p-4" onClick={() => setShowSavedPanel(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-lg w-full max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
              <h3 className="font-semibold text-gray-900 dark:text-white">Saved Itineraries</h3>
              <button onClick={() => setShowSavedPanel(false)}><XMarkIcon className="w-5 h-5 text-gray-500" /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-3 space-y-2">
              {isLoadingList && <div className="text-center py-8 text-sm text-gray-400">Loading…</div>}
              {!isLoadingList && savedList.length === 0 && (
                <div className="text-center py-8 text-sm text-gray-400">No saved itineraries yet</div>
              )}
              {savedList.map(item => (
                <div key={item.id} className="flex items-start gap-2 p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-teal-300 transition-colors">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm text-gray-900 dark:text-white truncate">{item.title}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{item.cities.slice(0, 3).join(' → ')}{item.city_count > 3 ? ` +${item.city_count - 3}` : ''}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{new Date(item.updated_at).toLocaleDateString()}</p>
                  </div>
                  <div className="flex flex-col gap-1">
                    <button onClick={() => loadItinerary(item.id)} className="px-2 py-1 text-xs text-teal-600 dark:text-teal-400 hover:bg-teal-50 dark:hover:bg-teal-900/20 rounded font-medium">Load</button>
                    <button onClick={() => deleteItinerary(item.id)} className="px-2 py-1 text-xs text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded font-medium">Delete</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ═══ PLAN MODE — simple input form ═══ */}
      {mode === 'plan' && (
        <div className="flex-1 overflow-hidden relative">
          <QuickPlanForm
            initial={planFormInitial}
            onGenerate={generateFromPlan}
            isGenerating={isGenerating}
            progressMessage={generatingMessage}
            errorMessage={error}
            onSwitchToAdvanced={() => setMode('build')}
          />
        </div>
      )}

      {/* ═══ EXPLORE MODE — full-map view with slide-in detail ═══ */}
      {mode === 'explore' && (
        <div className="flex-1 relative overflow-hidden">
          <ItineraryMapV2
            segments={itin.segments}
            sideTrips={itin.side_trips}
            selectedStayId={selectedStayId}
            selectedTransitId={selectedTransitId}
            onStayClick={id => { setSelectedStayId(id); setSelectedTransitId(null); }}
            onTransitClick={id => { setSelectedTransitId(id); setSelectedStayId(null); }}
            showRouteSights
            className="absolute inset-0"
          />

          {/* Empty-state overlay if nothing's been generated yet */}
          {stays.length === 0 || !stays.some(s => s.location.city) ? (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-50/80 dark:bg-gray-900/80 z-[550]">
              <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-6 max-w-sm text-center">
                <MapPinIcon className="w-10 h-10 text-teal-500 mx-auto mb-3" />
                <p className="text-sm text-gray-700 dark:text-gray-200">
                  Nothing to explore yet. Switch to Plan to build an itinerary, or load a saved trip.
                </p>
                <button onClick={() => setMode('plan')}
                  className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 rounded-lg">
                  📝 Go to Plan
                </button>
              </div>
            </div>
          ) : null}

          {/* Selected-stay side panel */}
          {selectedStayId && (() => {
            const stay = stays.find(s => s.id === selectedStayId);
            if (!stay) return null;
            const idx = stays.indexOf(stay);
            return (
              <StayExplorePanel
                stay={stay}
                stayIndex={idx}
                colorHex={stayColorFor(idx)}
                onClose={() => setSelectedStayId(null)}
                onEnrich={() => enrichStay(stay.id)}
                isEnriching={enrichingId === stay.id}
              />
            );
          })()}

          {/* Selected-transit side panel */}
          {selectedTransitId && (() => {
            const t = itin.segments.find(s => s.id === selectedTransitId) as TransitSegment | undefined;
            if (!t || t.type !== 'transit') return null;
            return (
              <TransitExplorePanel
                transit={t}
                allowedModes={allowedModes}
                onClose={() => setSelectedTransitId(null)}
                onChangeMode={m => changeTransitMode(t.id, m)}
                onLoadRouteSights={() => loadRouteSightsFor(t.id)}
                isRecalculating={calcTransitId === t.id}
                isLoadingRouteSights={loadingRouteSightsId === t.id}
              />
            );
          })()}

          {/* Bottom timeline strip */}
          {stays.length > 0 && stays.some(s => s.location.city) && (
            <ExploreBottomStrip
              segments={itin.segments}
              selectedStayId={selectedStayId}
              selectedTransitId={selectedTransitId}
              onSelectStay={id => { setSelectedStayId(id); setSelectedTransitId(null); }}
              onSelectTransit={id => { setSelectedTransitId(id); setSelectedStayId(null); }}
              stayColor={stayColorFor}
            />
          )}

          {/* Inline generating banner */}
          {isGenerating && generatingMessage && (
            <div className="absolute top-3 left-1/2 -translate-x-1/2 z-[550] bg-teal-600 text-white px-4 py-2 rounded-full shadow-lg flex items-center gap-2 text-sm">
              <ArrowPathIcon className="w-4 h-4 animate-spin" />
              {generatingMessage}
            </div>
          )}
        </div>
      )}

      {/* ═══ BUILD / VIEW MODES — original two-panel editor ═══ */}
      {(mode === 'build' || mode === 'view') && (
      <div className="flex flex-1 overflow-hidden relative">

        {/* ═══ LEFT PANEL ═══ */}
        <div className="w-[420px] flex-shrink-0 flex flex-col overflow-hidden border-r border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 relative">

          {/* Saved panel overlay */}
          {showSavedPanel && (
            <div className="absolute inset-0 z-20 bg-white dark:bg-gray-800 flex flex-col">
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                <h3 className="font-semibold text-gray-900 dark:text-white">Saved Itineraries</h3>
                <button onClick={() => setShowSavedPanel(false)}><XMarkIcon className="w-5 h-5 text-gray-500" /></button>
              </div>
              <div className="flex-1 overflow-y-auto p-3 space-y-2">
                {isLoadingList && <div className="text-center py-8 text-sm text-gray-400">Loading…</div>}
                {!isLoadingList && savedList.length === 0 && (
                  <div className="text-center py-8 text-sm text-gray-400">No saved itineraries yet</div>
                )}
                {savedList.map(item => (
                  <div key={item.id} className="flex items-start gap-2 p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-teal-300 transition-colors">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm text-gray-900 dark:text-white truncate">{item.title}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{item.cities.slice(0, 3).join(' → ')}{item.city_count > 3 ? ` +${item.city_count - 3}` : ''}</p>
                      <p className="text-xs text-gray-400 mt-0.5">{new Date(item.updated_at).toLocaleDateString()}</p>
                    </div>
                    <div className="flex flex-col gap-1">
                      <button onClick={() => loadItinerary(item.id)} className="px-2 py-1 text-xs text-teal-600 dark:text-teal-400 hover:bg-teal-50 dark:hover:bg-teal-900/20 rounded font-medium">Load</button>
                      <button onClick={() => deleteItinerary(item.id)} className="px-2 py-1 text-xs text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded font-medium">Delete</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* BUILD MODE */}
          {mode === 'build' && (
            <div className="flex-1 overflow-y-auto">
              {/* Preferences */}
              <div className="p-4 border-b border-gray-100 dark:border-gray-700">
                <SectionLabel>Trip preferences</SectionLabel>
                <div className="grid grid-cols-3 gap-2 mb-2">
                  {[
                    { label: 'Style', val: travelStyle, opts: TRAVEL_STYLES, set: setTravelStyle },
                    { label: 'Budget', val: budgetLevel, opts: BUDGET_LEVELS, set: setBudgetLevel },
                    { label: 'Group', val: groupType, opts: GROUP_TYPES, set: setGroupType },
                  ].map(({ label, val, opts, set }) => (
                    <div key={label}>
                      <label className="block text-xs text-gray-500 mb-1">{label}</label>
                      <select value={val} onChange={e => set(e.target.value)}
                        className="w-full text-xs border border-gray-300 dark:border-gray-600 rounded-lg px-2 py-1.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-teal-500">
                        {opts.map(o => <option key={o} value={o}>{o.charAt(0).toUpperCase() + o.slice(1)}</option>)}
                      </select>
                    </div>
                  ))}
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Interests</label>
                  <div className="flex flex-wrap gap-1">
                    {INTERESTS.map(i => (
                      <Pill key={i} active={interests.includes(i)}
                        onClick={() => setInterests(prev => prev.includes(i) ? prev.filter(x => x !== i) : [...prev, i])}>
                        {i}
                      </Pill>
                    ))}
                  </div>
                </div>
              </div>

              {/* Segments */}
              <div className="p-3 space-y-2">
                {itin.segments.map((seg, segIdx) => {
                  if (seg.type === 'stay') {
                    const stayIdx = stays.indexOf(seg as Stay);
                    return (
                      <StayCard
                        key={seg.id}
                        stay={seg as Stay}
                        stayIndex={stayIdx}
                        isFirst={segIdx === 0}
                        isExpanded={expandedStayId === seg.id}
                        isSelected={selectedStayId === seg.id}
                        enrichingId={enrichingId}
                        loadingSideTrips={loadingSideTrips}
                        existingSideTrips={itin.side_trips.filter(s => s.from_stay_id === seg.id)}
                        onSelect={() => setSelectedStayId(seg.id)}
                        onToggleExpand={() => setExpandedStayId(p => p === seg.id ? null : seg.id)}
                        onUpdateLocation={loc => {
                          updateSegment(seg.id, { location: loc });
                          const nextTransit = itin.segments[segIdx + 1] as TransitSegment | undefined;
                          if (nextTransit?.type === 'transit') updateSegment(nextTransit.id, { from_location: loc });
                        }}
                        onUpdateCheckIn={ci => updateStayCheckIn(seg.id, ci)}
                        onUpdateNights={n => updateStayNights(seg.id, n)}
                        onUpdateNotes={notes => updateSegment(seg.id, { notes })}
                        onUpdateLodging={lodging => updateSegment(seg.id, { lodging })}
                        onEnrich={() => enrichStay(seg.id)}
                        onFetchSideTrips={() => fetchSideTrips(seg.id)}
                        onAddSideTrip={sg => addSideTrip(seg.id, sg)}
                        onRemoveSideTrip={id => removeSideTrip(id)}
                        onRemove={stayIdx > 0 ? () => removeStop(seg.id) : undefined}
                      />
                    );
                  }
                  return (
                    <TransitCard
                      key={seg.id}
                      transit={seg as TransitSegment}
                      isExpanded={expandedTransitId === seg.id}
                      isCalculating={calcTransitId === seg.id}
                      onToggleExpand={() => setExpandedTransitId(p => p === seg.id ? null : seg.id)}
                      onUpdateMode={m => updateSegment(seg.id, { mode: m })}
                      onUpdateTo={loc => updateTransitTo(seg.id, loc)}
                      onCalculate={() => calculateTransit(seg.id)}
                      onUpdateNotes={notes => updateSegment(seg.id, { notes })}
                      onUpdateDeparture={dep => updateTransitDeparture(seg.id, dep)}
                      onClearDepartureOverride={() => clearTransitDeparture(seg.id)}
                    />
                  );
                })}

                <button onClick={addStop}
                  className="w-full flex items-center justify-center gap-2 py-2.5 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-500 dark:text-gray-400 hover:border-teal-400 hover:text-teal-600 transition-colors">
                  <PlusIcon className="w-4 h-4" /> Add next destination
                </button>

                <div className="pt-2 border-t border-gray-100 dark:border-gray-700">
                  <button onClick={loadTravelRequirements} disabled={loadingTravelReqs}
                    className="w-full flex items-center justify-center gap-2 py-2 text-sm font-medium text-teal-700 dark:text-teal-300 bg-teal-50 dark:bg-teal-900/20 hover:bg-teal-100 dark:hover:bg-teal-900/30 rounded-lg disabled:opacity-60">
                    {loadingTravelReqs ? <ArrowPathIcon className="w-4 h-4 animate-spin" /> : <GlobeAltIcon className="w-4 h-4" />}
                    Load Travel Requirements & Entry Info
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* VIEW MODE */}
          {mode === 'view' && (
            <TimelinePanel
              timelineDays={timelineDays}
              sideTrips={itin.side_trips}
              travelRequirements={itin.travel_requirements}
              selectedStayId={selectedStayId}
              onSelectStay={id => setSelectedStayId(id)}
            />
          )}
        </div>

        {/* ═══ RIGHT PANEL — MAP ═══ */}
        <div className="flex-1 relative overflow-hidden">
          <ItineraryMapV2
            segments={itin.segments}
            sideTrips={itin.side_trips}
            selectedStayId={selectedStayId}
            onStayClick={id => { setSelectedStayId(id); setExpandedStayId(id); }}
            className="absolute inset-0"
          />
          {mode === 'build' && stays.some(s => !s.enriched && s.location.city && s.check_in && s.check_out) && (
            <div className="absolute top-3 right-3 z-[500]">
              <button
                onClick={async () => {
                  for (const stay of stays) {
                    if (!stay.enriched && stay.location.city && stay.check_in && stay.check_out) await enrichStay(stay.id);
                  }
                }}
                disabled={!!enrichingId}
                className="flex items-center gap-1.5 px-3 py-2 bg-teal-600 hover:bg-teal-700 text-white text-sm font-medium rounded-lg shadow-lg disabled:opacity-60"
              >
                {enrichingId ? <ArrowPathIcon className="w-4 h-4 animate-spin" /> : <SparklesIcon className="w-4 h-4" />}
                Enrich All Stays
              </button>
            </div>
          )}
        </div>
      </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// StayCard
// ─────────────────────────────────────────────────────────────────────────────

const STAY_COLORS_CSS = ['#0E7490','#7C3AED','#B45309','#065F46','#9F1239','#1D4ED8'];

interface StayCardProps {
  stay: Stay; stayIndex: number; isFirst: boolean;
  isExpanded: boolean; isSelected: boolean;
  enrichingId: string | null; loadingSideTrips: string | null;
  existingSideTrips: SideTrip[];
  onSelect: () => void; onToggleExpand: () => void;
  onUpdateLocation: (l: ItinLocation) => void;
  onUpdateCheckIn: (v: string) => void; onUpdateNights: (n: number) => void;
  onUpdateNotes: (v: string) => void; onUpdateLodging: (l: Stay['lodging']) => void;
  onEnrich: () => void; onFetchSideTrips: () => void;
  onAddSideTrip: (s: SideTripSuggestion) => void; onRemoveSideTrip: (id: string) => void;
  onRemove?: () => void;
}

const StayCard: React.FC<StayCardProps> = ({
  stay, stayIndex, isFirst, isExpanded, isSelected, enrichingId, loadingSideTrips,
  existingSideTrips, onSelect, onToggleExpand, onUpdateLocation, onUpdateCheckIn,
  onUpdateNights, onUpdateNotes, onUpdateLodging, onEnrich, onFetchSideTrips,
  onAddSideTrip, onRemoveSideTrip, onRemove,
}) => {
  const color = STAY_COLORS_CSS[stayIndex % STAY_COLORS_CSS.length];
  const isEnriching = enrichingId === stay.id;
  const suggestions: SideTripSuggestion[] = (stay as any)._sideTripSuggestions || [];

  return (
    <div onClick={onSelect}
      className={`rounded-xl border-2 transition-colors cursor-pointer ${isSelected ? 'border-teal-500 shadow-md' : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'} bg-white dark:bg-gray-800`}>

      <div className="flex items-start gap-2 p-3" onClick={e => { e.stopPropagation(); onToggleExpand(); }}>
        <div className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0 mt-0.5" style={{ backgroundColor: color }}>
          {stayIndex + 1}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="font-semibold text-sm text-gray-900 dark:text-white">{stay.location.city || 'New Destination'}</span>
            {stay.location.country && <span className="text-xs text-gray-400">{stay.location.country}</span>}
            {stay.enriched && <Badge color="green">✓</Badge>}
          </div>
          <div className="flex items-center gap-2 mt-0.5 text-xs text-gray-400 flex-wrap">
            {stay.check_in && <span>{formatDate(stay.check_in)}</span>}
            {stay.nights > 0 && <span>🌙 {stay.nights}n</span>}
            {stay.activities.length > 0 && <span>{stay.activities.length} activities</span>}
          </div>
        </div>
        <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
          {onRemove && (
            <button onClick={onRemove} className="p-1 text-gray-400 hover:text-red-500 rounded">
              <TrashIcon className="w-4 h-4" />
            </button>
          )}
          <button onClick={e => { e.stopPropagation(); onToggleExpand(); }} className="p-1 text-gray-400">
            {isExpanded ? <ChevronUpIcon className="w-4 h-4" /> : <ChevronDownIcon className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {isExpanded && (
        <div className="border-t border-gray-100 dark:border-gray-700 p-3 space-y-3" onClick={e => e.stopPropagation()}>
          <div>
            <SectionLabel>Location</SectionLabel>
            <LocationSearch value={stay.location} onChange={onUpdateLocation} placeholder="City, Country" />
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <SectionLabel>{isFirst ? 'Arrival date/time' : 'Check-in (auto)'}</SectionLabel>
              <input type="datetime-local" value={stay.check_in} onChange={e => onUpdateCheckIn(e.target.value)}
                className="w-full text-xs border border-gray-300 dark:border-gray-600 rounded-lg px-2 py-1.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-teal-500" />
            </div>
            <div>
              <SectionLabel>Nights</SectionLabel>
              <div className="flex items-center gap-2">
                <button onClick={() => onUpdateNights(Math.max(0, stay.nights - 1))}
                  className="w-7 h-7 rounded-full border border-gray-300 dark:border-gray-600 flex items-center justify-center text-gray-600 dark:text-gray-300 hover:bg-gray-50">−</button>
                <span className="text-sm font-bold text-gray-900 dark:text-white w-5 text-center">{stay.nights}</span>
                <button onClick={() => onUpdateNights(stay.nights + 1)}
                  className="w-7 h-7 rounded-full border border-gray-300 dark:border-gray-600 flex items-center justify-center text-gray-600 dark:text-gray-300 hover:bg-gray-50">+</button>
                {stay.check_out && <span className="text-xs text-gray-400 truncate">→ {formatDate(stay.check_out)}</span>}
              </div>
            </div>
          </div>

          <div>
            <SectionLabel>Lodging</SectionLabel>
            <div className="space-y-1.5">
              <label className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300">
                <input type="checkbox" checked={stay.lodging.want_recommendations}
                  onChange={e => onUpdateLodging({ ...stay.lodging, want_recommendations: e.target.checked })} className="rounded" />
                Get AI lodging recommendations
              </label>
              <input type="text" value={stay.lodging.specified_name || ''} onChange={e => onUpdateLodging({ ...stay.lodging, specified_name: e.target.value })}
                placeholder="Or enter hotel name (optional)"
                className="w-full text-xs border border-gray-300 dark:border-gray-600 rounded-lg px-2 py-1.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-teal-500" />
            </div>
          </div>

          <div>
            <SectionLabel>Notes for AI</SectionLabel>
            <textarea value={stay.notes || ''} onChange={e => onUpdateNotes(e.target.value)} rows={2}
              placeholder="e.g. Love walking old towns, want seafood dinners, skip museums…"
              className="w-full text-xs border border-gray-300 dark:border-gray-600 rounded-lg px-2 py-1.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-teal-500 resize-none" />
          </div>

          <div className="flex gap-2">
            <button onClick={onEnrich} disabled={isEnriching || !stay.location.city || !stay.check_in}
              className="flex-1 flex items-center justify-center gap-1.5 py-1.5 text-xs font-medium text-white bg-teal-600 hover:bg-teal-700 rounded-lg disabled:opacity-50">
              {isEnriching ? <ArrowPathIcon className="w-3.5 h-3.5 animate-spin" /> : <SparklesIcon className="w-3.5 h-3.5" />}
              {isEnriching ? 'Enriching…' : stay.enriched ? 'Re-enrich' : 'Enrich with AI'}
            </button>
            <button onClick={onFetchSideTrips} disabled={loadingSideTrips === stay.id || !stay.location.city}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-teal-700 dark:text-teal-300 bg-teal-50 dark:bg-teal-900/20 hover:bg-teal-100 rounded-lg disabled:opacity-50">
              {loadingSideTrips === stay.id ? <ArrowPathIcon className="w-3.5 h-3.5 animate-spin" /> : <MapPinIcon className="w-3.5 h-3.5" />}
              Side trips
            </button>
          </div>

          {/* Enrichment preview */}
          {stay.enriched && stay.activities.length > 0 && (
            <div className="bg-gray-50 dark:bg-gray-700/40 rounded-lg p-2.5">
              <p className="text-xs font-semibold text-gray-500 mb-1.5">Activities preview</p>
              {stay.activities.slice(0, 4).map(a => (
                <div key={a.id} className="flex items-center gap-2 py-0.5">
                  <Badge color="gray">{a.time_slot.slice(0,3)}</Badge>
                  <span className="text-xs text-gray-700 dark:text-gray-300 truncate">{a.title}</span>
                </div>
              ))}
              {stay.activities.length > 4 && <p className="text-xs text-gray-400 mt-1">+{stay.activities.length - 4} more</p>}
              {stay.lodging.recommendations?.length ? <p className="text-xs text-teal-600 mt-1">🏨 {stay.lodging.recommendations.length} lodging options</p> : null}
              {stay.dining_recommendations.length > 0 && <p className="text-xs text-teal-600">🍽️ {stay.dining_recommendations.length} dining recommendations</p>}
            </div>
          )}

          {/* Side trip suggestions */}
          {suggestions.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1.5">Side trips from {stay.location.city}</p>
              <div className="space-y-2">
                {suggestions.map(sg => {
                  const added = existingSideTrips.some(s => s.suggestion.destination === sg.destination);
                  return (
                    <div key={sg.id} className="border border-gray-200 dark:border-gray-600 rounded-lg p-2">
                      <div className="flex items-start gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="text-xs font-semibold text-gray-900 dark:text-white">{sg.destination}</span>
                            <Badge color={sg.duration === 'half-day' ? 'gray' : 'purple'}>{sg.duration}</Badge>
                            {sg.border_crossing && <Badge color="amber">⚠ Border</Badge>}
                          </div>
                          {sg.driving_time_minutes && <p className="text-xs text-gray-400">🚗 {formatDuration(sg.driving_time_minutes)}</p>}
                          <p className="text-xs text-gray-600 dark:text-gray-300 mt-0.5 line-clamp-2">{sg.description}</p>
                          {sg.border_notes && <p className="text-xs text-amber-600 mt-0.5">{sg.border_notes}</p>}
                        </div>
                        <button onClick={() => !added && onAddSideTrip(sg)} disabled={added}
                          className={`flex-shrink-0 px-2 py-1 text-xs rounded-lg font-medium ${added ? 'bg-green-100 text-green-600' : 'bg-teal-600 text-white hover:bg-teal-700'}`}>
                          {added ? '✓' : '+'}
                        </button>
                      </div>
                      <div className="flex flex-wrap gap-1 mt-1.5">
                        {sg.highlights.slice(0, 3).map((h, i) => (
                          <span key={i} className="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-xs rounded">{h}</span>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Existing side trips */}
          {existingSideTrips.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-gray-500 mb-1">Confirmed side trips</p>
              {existingSideTrips.map(st => (
                <div key={st.id} className="flex items-center justify-between py-1 border-b border-gray-100 dark:border-gray-700 last:border-0">
                  <span className="text-xs text-gray-700 dark:text-gray-300">↪ {st.suggestion.destination}</span>
                  <button onClick={() => onRemoveSideTrip(st.id)} className="text-red-400 hover:text-red-500 p-0.5"><XMarkIcon className="w-3.5 h-3.5" /></button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// TransitCard
// ─────────────────────────────────────────────────────────────────────────────

interface TransitCardProps {
  transit: TransitSegment; isExpanded: boolean; isCalculating: boolean;
  onToggleExpand: () => void; onUpdateMode: (m: TransitMode) => void;
  onUpdateTo: (l: ItinLocation) => void; onCalculate: () => void; onUpdateNotes: (v: string) => void;
  onUpdateDeparture: (departure: string) => void;
  onClearDepartureOverride: () => void;
}

const TransitCard: React.FC<TransitCardProps> = ({
  transit, isExpanded, isCalculating, onToggleExpand, onUpdateMode, onUpdateTo, onCalculate, onUpdateNotes,
  onUpdateDeparture, onClearDepartureOverride,
}) => {
  const modeInfo = TRANSIT_MODES.find(m => m.value === transit.mode) || TRANSIT_MODES[0];
  const hasCalc = !!transit.duration_minutes;

  return (
    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl border border-gray-200 dark:border-gray-600">
      <div className="flex items-center gap-2 px-3 py-2 cursor-pointer" onClick={onToggleExpand}>
        <span className="text-base flex-shrink-0">{modeInfo.icon}</span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-medium text-gray-600 dark:text-gray-300 truncate">
              {transit.from_location.city || '?'} → {transit.to_location.city || '?'}
            </span>
            {hasCalc && (
              <Badge color="teal">
                {formatDuration(transit.duration_minutes!)}
                {transit.distance_km ? ` · ${Math.round(transit.distance_km)}km` : ''}
              </Badge>
            )}
          </div>
          {transit.departure_time && (
            <span className="text-xs text-gray-400">{formatDT(transit.departure_time)}</span>
          )}
        </div>
        <button className="p-1 text-gray-400">
          {isExpanded ? <ChevronUpIcon className="w-3.5 h-3.5" /> : <ChevronDownIcon className="w-3.5 h-3.5" />}
        </button>
      </div>

      {isExpanded && (
        <div className="border-t border-gray-200 dark:border-gray-600 p-3 space-y-3">
          <div>
            <SectionLabel>Destination</SectionLabel>
            <LocationSearch value={transit.to_location} onChange={onUpdateTo} placeholder="Destination city, country" />
          </div>
          <div>
            <SectionLabel>Mode</SectionLabel>
            <div className="flex gap-1.5 flex-wrap">
              {TRANSIT_MODES.map(m => (
                <button key={m.value} onClick={() => onUpdateMode(m.value)}
                  className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                    transit.mode === m.value ? 'bg-teal-600 text-white border-teal-600' : 'bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:border-teal-400'
                  }`}>
                  {m.icon} {m.label}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <SectionLabel>Departure</SectionLabel>
              <input
                type="datetime-local"
                value={transit.departure_time || ''}
                onChange={e => onUpdateDeparture(e.target.value)}
                className="w-full text-xs border border-gray-300 dark:border-gray-600 rounded-lg px-2 py-1.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-teal-500"
              />
              {transit.departure_override && (
                <button
                  onClick={onClearDepartureOverride}
                  className="mt-1 text-xs text-gray-400 hover:text-teal-600 dark:hover:text-teal-400 flex items-center gap-0.5"
                  title="Reset to previous stay's checkout time"
                >
                  <ArrowPathIcon className="w-2.5 h-2.5" /> Sync to checkout
                </button>
              )}
            </div>
            <div>
              <SectionLabel>Arrival {!hasCalc && <span className="text-gray-300 dark:text-gray-600 normal-case font-normal">(calculate first)</span>}</SectionLabel>
              <div className="text-xs text-gray-700 dark:text-gray-300 py-1.5 px-2 bg-gray-100 dark:bg-gray-600/50 rounded-lg min-h-[30px] flex items-center">
                {transit.arrival_time ? formatDT(transit.arrival_time) : '—'}
              </div>
            </div>
          </div>
          <div>
            <SectionLabel>Notes (optional)</SectionLabel>
            <input type="text" value={transit.notes || ''} onChange={e => onUpdateNotes(e.target.value)}
              placeholder="e.g. Scenic coastal route via Makarska"
              className="w-full text-xs border border-gray-300 dark:border-gray-600 rounded-lg px-2 py-1.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-teal-500" />
          </div>
          <button onClick={onCalculate} disabled={isCalculating}
            className="w-full flex items-center justify-center gap-1.5 py-1.5 text-xs font-medium text-teal-700 dark:text-teal-300 bg-teal-50 dark:bg-teal-900/20 hover:bg-teal-100 rounded-lg disabled:opacity-50">
            {isCalculating ? <ArrowPathIcon className="w-3.5 h-3.5 animate-spin" /> : <ClockIcon className="w-3.5 h-3.5" />}
            {isCalculating ? 'Calculating…' : hasCalc ? 'Recalculate' : 'Calculate travel time'}
          </button>
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// TimelinePanel (view mode)
// ─────────────────────────────────────────────────────────────────────────────

interface TimelinePanelProps {
  timelineDays: any[];
  sideTrips: SideTrip[];
  travelRequirements?: TravelRequirement[];
  selectedStayId: string | null;
  onSelectStay: (id: string) => void;
}

const TIME_SLOT_COLORS: Record<string, string> = {
  morning: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  afternoon: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  evening: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
};

const TimelinePanel: React.FC<TimelinePanelProps> = ({
  timelineDays, sideTrips, travelRequirements, selectedStayId, onSelectStay,
}) => {
  const [expandedDayIdx, setExpandedDayIdx] = useState<number | null>(null);
  const [expandedReq, setExpandedReq] = useState<string | null>(null);

  return (
    <div className="flex-1 overflow-y-auto p-3 space-y-2">
      {timelineDays.length === 0 && (
        <div className="text-center py-12 text-sm text-gray-400">Switch to Build mode to plan your trip</div>
      )}

      {timelineDays.map((day: any, idx: number) => {
        if (day.type === 'transit') {
          return (
            <div key={idx} className="flex items-start gap-2 px-2 py-1.5 text-sm">
              <span className="text-lg flex-shrink-0 mt-0.5">🚗</span>
              <div>
                <span className="font-medium text-gray-700 dark:text-gray-300">{day.dayLabel}</span>
                {day.transit?.duration_minutes && (
                  <span className="text-xs text-gray-400 ml-2">{formatDuration(day.transit.duration_minutes)}{day.transit.distance_km ? ` · ${Math.round(day.transit.distance_km)}km` : ''}</span>
                )}
                {day.transit?.departure_time && (
                  <p className="text-xs text-gray-400">{formatDT(day.transit.departure_time)} → {formatDT(day.transit.arrival_time)}</p>
                )}
                {day.transit?.notes && <p className="text-xs text-gray-400 italic">{day.transit.notes}</p>}
              </div>
            </div>
          );
        }

        const isSelected = day.stay?.id === selectedStayId;
        const daySideTrips = day.stay ? sideTrips.filter((s: SideTrip) => s.from_stay_id === day.stay!.id) : [];

        return (
          <div key={idx} className={`rounded-xl border-2 transition-colors bg-white dark:bg-gray-800 ${isSelected ? 'border-teal-400' : 'border-gray-200 dark:border-gray-700'}`}>
            <div className="flex items-start gap-2 p-3 cursor-pointer"
              onClick={() => { setExpandedDayIdx(p => p === idx ? null : idx); if (day.stay) onSelectStay(day.stay.id); }}>
              <div className="flex-shrink-0 text-center w-12">
                <p className="text-xs font-bold text-teal-600 dark:text-teal-400 uppercase">
                  {day.date ? new Date(day.date + 'T12:00').toLocaleDateString('en-US', { month: 'short' }) : ''}
                </p>
                <p className="text-xl font-bold text-gray-900 dark:text-white leading-tight">
                  {day.date ? new Date(day.date + 'T12:00').getDate() : ''}
                </p>
                <p className="text-xs text-gray-400">
                  {day.date ? new Date(day.date + 'T12:00').toLocaleDateString('en-US', { weekday: 'short' }) : ''}
                </p>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="font-semibold text-sm text-gray-900 dark:text-white">{day.dayLabel}</span>
                  {day.isArrival && <Badge color="teal">Arrive</Badge>}
                  {day.isDeparture && <Badge color="gray">Depart</Badge>}
                  {daySideTrips.length > 0 && <Badge color="amber">↪ Side trip</Badge>}
                </div>
                <p className="text-xs text-gray-400 mt-0.5">
                  {day.activities.length > 0 ? `${day.activities.length} activities` : 'No activities planned'}
                </p>
              </div>
              <button className="p-1 text-gray-400">
                {expandedDayIdx === idx ? <ChevronUpIcon className="w-4 h-4" /> : <ChevronDownIcon className="w-4 h-4" />}
              </button>
            </div>

            {expandedDayIdx === idx && (
              <div className="border-t border-gray-100 dark:border-gray-700 p-3 space-y-3">
                {day.isArrival && day.stay?.check_in && (
                  <p className="text-xs text-teal-600 flex items-center gap-1">
                    <CheckCircleIcon className="w-4 h-4" />
                    Arrive {formatDT(day.stay.check_in)}{day.stay.lodging.specified_name ? ` · ${day.stay.lodging.specified_name}` : ''}
                  </p>
                )}

                {['morning', 'afternoon', 'evening'].map(slot => {
                  const acts = day.activities.filter((a: ItinActivity) => a.time_slot === slot);
                  if (!acts.length) return null;
                  return (
                    <div key={slot}>
                      <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5 capitalize">{slot}</p>
                      <div className="space-y-2">
                        {acts.map((a: ItinActivity) => (
                          <div key={a.id} className="flex gap-2">
                            <span className={`flex-shrink-0 px-1.5 py-0.5 rounded text-xs font-medium h-fit ${TIME_SLOT_COLORS[slot]}`}>{a.duration_hours}h</span>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start gap-1">
                                <p className="text-sm font-medium text-gray-900 dark:text-white flex-1">{a.title}</p>
                                {a.url && <a href={a.url} target="_blank" rel="noopener noreferrer" className="flex-shrink-0"><LinkIcon className="w-3.5 h-3.5 text-blue-400 hover:text-blue-600" /></a>}
                              </div>
                              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{a.description}</p>
                              {(a.estimated_cost_usd ?? 0) > 0 && <p className="text-xs text-gray-400">~${a.estimated_cost_usd}</p>}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}

                {day.activities.length === 0 && !day.isDeparture && (
                  <p className="text-xs text-gray-400 italic">No activities — go to Build mode and click Enrich.</p>
                )}

                {daySideTrips.length > 0 && (
                  <div className="bg-amber-50 dark:bg-amber-900/10 rounded-lg p-2.5">
                    <p className="text-xs font-semibold text-amber-700 dark:text-amber-400 mb-1">↪ Side trips</p>
                    {daySideTrips.map((st: SideTrip) => (
                      <div key={st.id} className="flex items-start gap-2 mt-1">
                        <MapPinIcon className="w-3.5 h-3.5 text-amber-500 flex-shrink-0 mt-0.5" />
                        <div className="text-xs text-amber-700 dark:text-amber-300">
                          <span className="font-medium">{st.suggestion.destination}</span>
                          {st.suggestion.driving_time_minutes && <span className="text-amber-500 ml-1">· {formatDuration(st.suggestion.driving_time_minutes)} drive</span>}
                          <p className="mt-0.5">{st.suggestion.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {day.isArrival && day.stay?.lodging.recommendations?.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-gray-500 mb-1.5">🏨 Lodging</p>
                    {day.stay.lodging.recommendations.map((r: LodgingRec, i: number) => (
                      <div key={i} className="border border-gray-200 dark:border-gray-600 rounded-lg p-2 mb-1.5">
                        <div className="flex items-start justify-between gap-1">
                          <div>
                            <span className="text-xs font-semibold text-gray-900 dark:text-white">{r.name}</span>
                            <span className="text-xs text-gray-400 ml-1">{r.type} · {r.area}</span>
                          </div>
                          <Badge color={r.price_level === 'luxury' ? 'purple' : r.price_level === 'budget' ? 'green' : 'gray'}>{r.price_level}</Badge>
                        </div>
                        <p className="text-xs text-gray-500 mt-0.5">{r.description}</p>
                        {r.search_url && <a href={r.search_url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-500 hover:underline mt-1 inline-flex items-center gap-1"><LinkIcon className="w-3 h-3" /> Search Booking.com</a>}
                      </div>
                    ))}
                  </div>
                )}

                {day.isArrival && day.stay?.dining_recommendations?.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-gray-500 mb-1.5">🍽️ Dining</p>
                    {day.stay.dining_recommendations.slice(0, 4).map((r: DiningRec, i: number) => (
                      <div key={i} className="flex items-start gap-2 py-1 border-b border-gray-100 dark:border-gray-700 last:border-0">
                        <div className="flex-1">
                          <span className="text-xs font-semibold text-gray-900 dark:text-white">{r.name}</span>
                          <span className="text-xs text-gray-400 ml-1">· {r.cuisine}</span>
                          <p className="text-xs text-gray-500">{r.description}</p>
                        </div>
                        <Badge color={r.price_level === 'luxury' ? 'purple' : r.price_level === 'budget' ? 'green' : 'gray'}>{r.price_level}</Badge>
                      </div>
                    ))}
                  </div>
                )}

                {day.isArrival && day.stay?.travel_tips?.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-gray-500 mb-1">💡 Local tips</p>
                    <ul className="space-y-1">
                      {day.stay.travel_tips.map((tip: string, i: number) => (
                        <li key={i} className="text-xs text-gray-600 dark:text-gray-300 flex items-start gap-1.5">
                          <span className="text-teal-500 flex-shrink-0">•</span>{tip}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}

      {/* Travel Requirements */}
      {travelRequirements && travelRequirements.length > 0 && (
        <div className="mt-4 rounded-xl border-2 border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
          <div className="flex items-center gap-2 p-3 border-b border-gray-100 dark:border-gray-700">
            <GlobeAltIcon className="w-5 h-5 text-teal-600" />
            <h3 className="font-semibold text-sm text-gray-900 dark:text-white">Travel Requirements & Entry Info</h3>
          </div>
          {travelRequirements.map((req, i) => (
            <div key={i}>
              <button
                className="w-full flex items-center justify-between p-3 hover:bg-gray-50 dark:hover:bg-gray-700/50"
                onClick={() => setExpandedReq(p => p === req.country ? null : req.country)}>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-sm text-gray-900 dark:text-white">{req.country}</span>
                  {req.schengen_member && <Badge color="teal">Schengen</Badge>}
                  {req.eu_member && !req.schengen_member && <Badge color="purple">EU</Badge>}
                  {req.safety_rating && (
                    <Badge color={req.safety_rating.startsWith('Very Safe') || req.safety_rating === 'Safe' ? 'green' : 'amber'}>
                      {req.safety_rating}
                    </Badge>
                  )}
                </div>
                <ChevronDownIcon className={`w-4 h-4 text-gray-400 transition-transform ${expandedReq === req.country ? 'rotate-180' : ''}`} />
              </button>
              {expandedReq === req.country && (
                <div className="px-4 pb-4 space-y-2 text-xs border-t border-gray-100 dark:border-gray-700">
                  {req.currency && <div><span className="font-semibold text-gray-600 dark:text-gray-400">Currency: </span>{req.currency}</div>}
                  {req.language && <div><span className="font-semibold text-gray-600 dark:text-gray-400">Language: </span>{req.language}</div>}
                  <div><p className="font-semibold text-gray-600 dark:text-gray-400 mb-0.5">Visa / Entry</p><p className="text-gray-700 dark:text-gray-300">{req.visa_info}</p></div>
                  {req.entry_requirements && <div><p className="font-semibold text-gray-600 dark:text-gray-400 mb-0.5">Entry Requirements</p><p className="text-gray-700 dark:text-gray-300">{req.entry_requirements}</p></div>}
                  {req.safety_notes && <div><p className="font-semibold text-gray-600 dark:text-gray-400 mb-0.5">Safety</p><p className="text-gray-700 dark:text-gray-300">{req.safety_notes}</p></div>}
                  {req.border_notes && (
                    <div className="bg-amber-50 dark:bg-amber-900/10 rounded-lg p-2">
                      <p className="font-semibold text-amber-700 dark:text-amber-400 mb-0.5">⚠ Border crossing</p>
                      <p className="text-amber-600 dark:text-amber-300">{req.border_notes}</p>
                    </div>
                  )}
                  {(req.practical_tips?.length ?? 0) > 0 && (
                    <div><p className="font-semibold text-gray-600 dark:text-gray-400 mb-0.5">Practical tips</p>
                      <ul className="space-y-1">
                        {(req.practical_tips ?? []).map((t: string, j: number) => (
                          <li key={j} className="flex items-start gap-1.5 text-gray-700 dark:text-gray-300"><span className="text-teal-500">•</span>{t}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {req.advisory_url && <a href={req.advisory_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-blue-500 hover:underline"><LinkIcon className="w-3 h-3" /> Official travel advisory</a>}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
